// Abo-Hooks. RLS:
// - kunde sieht/erstellt/löscht eigene Abos
// - kunde updated eigene Abos (Pause, Items ändern)
// - staff verwaltet alle

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";
import type { PaymentMethod } from "@/lib/orders";

export interface Subscription {
  id: string;
  customer_id: string;
  address_id: string;
  weekdays: number[];          // 1=Mo..6=Sa
  interval_weeks: number;
  starts_on: string;           // YYYY-MM-DD
  ends_on: string | null;
  paused_from: string | null;
  paused_until: string | null;
  payment_method: PaymentMethod;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionItem {
  id: string;
  subscription_id: string;
  product_id: string;
  qty: number;
}

export interface SubscriptionWithJoins extends Subscription {
  items: SubscriptionItem[];
  customer?: { display_name: string } | null;
  address?: { street: string; zip: string; city: string } | null;
}

// ---------- Hooks für Kunden (eigene) ----------

export function useMySubscriptions() {
  return useQuery({
    queryKey: ["my-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select(`*, items:subscription_items(*), address:customer_addresses(street, zip, city)`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SubscriptionWithJoins[];
    },
  });
}

// ---------- Hooks für Backoffice (alle) ----------

export function useAllSubscriptions() {
  return useQuery({
    queryKey: ["all-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select(`
          *,
          items:subscription_items(*),
          customer:customers(display_name),
          address:customer_addresses(street, zip, city)
        `)
        .order("is_active", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SubscriptionWithJoins[];
    },
  });
}

// ---------- Mutations ----------

export interface CreateSubscriptionInput {
  customer_id: string;
  address_id: string;
  weekdays: number[];
  interval_weeks: number;
  starts_on: string;
  ends_on?: string | null;
  payment_method: PaymentMethod;
  notes?: string | null;
  items: Array<{ product_id: string; qty: number }>;
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSubscriptionInput) => {
      const { data: sub, error } = await supabase
        .from("subscriptions")
        .insert({
          customer_id: input.customer_id,
          address_id: input.address_id,
          weekdays: input.weekdays,
          interval_weeks: input.interval_weeks,
          starts_on: input.starts_on,
          ends_on: input.ends_on ?? null,
          payment_method: input.payment_method,
          notes: input.notes ?? null,
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;
      const itemRows = input.items.map((i) => ({
        subscription_id: sub.id,
        product_id: i.product_id,
        qty: i.qty,
      }));
      const { error: itemsErr } = await supabase.from("subscription_items").insert(itemRows);
      if (itemsErr) throw itemsErr;
      return sub as Subscription;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-subscriptions"] });
      qc.invalidateQueries({ queryKey: ["all-subscriptions"] });
    },
  });
}

export function usePauseSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, from, until }: { id: string; from: string | null; until: string | null }) => {
      const { error } = await supabase
        .from("subscriptions")
        .update({ paused_from: from, paused_until: until })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-subscriptions"] });
      qc.invalidateQueries({ queryKey: ["all-subscriptions"] });
    },
  });
}

export function useDeactivateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("subscriptions")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-subscriptions"] });
      qc.invalidateQueries({ queryKey: ["all-subscriptions"] });
    },
  });
}

// ---------- Helpers ----------

export const WEEKDAY_LABEL: Record<number, string> = {
  1: "Mo", 2: "Di", 3: "Mi", 4: "Do", 5: "Fr", 6: "Sa",
};

export function describeSchedule(s: { weekdays: number[]; interval_weeks: number }): string {
  const days = s.weekdays.sort().map((w) => WEEKDAY_LABEL[w]).join(", ");
  if (s.interval_weeks === 1) return `Jeden ${days}`;
  if (s.interval_weeks === 2) return `Alle 2 Wochen ${days}`;
  return `Alle ${s.interval_weeks} Wochen ${days}`;
}

export function isPausedOn(s: Subscription, date: Date): boolean {
  if (!s.paused_from || !s.paused_until) return false;
  const iso = date.toISOString().slice(0, 10);
  return iso >= s.paused_from && iso <= s.paused_until;
}

// ══════════════════════════════════════════════════════════════════ Turnus
//
// Rhythmus (interval_weeks) + individueller Turnusstart (starts_on) — der Fahrer
// legt beides selbst fest, die Tourplanung berücksichtigt es automatisch. Die
// Wiederkehr rechnet dieselbe Formel wie der Server (abo-faelligkeit.ts):
//   floor(Tage(starts_on → Tag) / 7) % interval_weeks === 0, am richtigen Wochentag.

export interface AboTakt {
  id: string;
  weekdays: number[];
  interval_weeks: number;
  starts_on: string; // YYYY-MM-DD
}

/** Das aktive Abo eines Kunden — Takt und Startpunkt. `null`, wenn keins da ist. */
export function useAboTakt(customerId: string | undefined) {
  return useQuery({
    queryKey: ["abo-takt", customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("id, weekdays, interval_weeks, starts_on")
        .eq("customer_id", customerId!)
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as AboTakt | null) ?? null;
    },
  });
}

/**
 * Turnus + Turnusstart bewusst neu setzen (verschiebt den Anker) und die nächsten
 * 7 Tage sofort abgleichen. Läuft über /api/tour?action=turnus-setzen, weil der
 * Abgleich mit dem Hauptschlüssel Bestellungen anlegt/entfernt und die Packliste
 * mitzieht — das gehört nicht in den Client.
 */
export function useTurnusSetzen() {
  const qc = useQueryClient();
  return useMutation({
    // Wie überall auf der Tour: ohne "always" pausiert die Mutation im Funkloch
    // still, und der Knopf tut scheinbar nichts.
    networkMode: "always",
    mutationFn: async (input: { customerId: string; intervalWeeks: number; startsOn: string }) => {
      const token = useAuth.getState().session?.access_token ?? "";
      const res = await fetch("/api/tour?action=turnus-setzen", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Turnus konnte nicht gesetzt werden");
      return res.json() as Promise<{ ok: true; interval_weeks: number; starts_on: string; hinzugefuegt: string[]; entfernt: string[] }>;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["abo-takt", v.customerId] });
      qc.invalidateQueries({ queryKey: ["customer", v.customerId] });
      qc.invalidateQueries({ queryKey: ["crm-customers"] });
      qc.invalidateQueries({ queryKey: ["all-subscriptions"] });
      qc.invalidateQueries({ queryKey: ["tour"] });
    },
  });
}

/** Rhythmus als Text: „Jede Woche" · „Alle 2 Wochen" … */
export function turnusText(intervalWeeks: number): string {
  if (intervalWeeks <= 1) return "Jede Woche";
  return `Alle ${intervalWeeks} Wochen`;
}

/**
 * Der nächste Liefertag ab `fromIso` (inklusive) — für die Vorschau „Nächste
 * Lieferung". Dieselbe Rechnung wie der Server, damit die Anzeige nicht lügt.
 * `null`, wenn in den nächsten 14 Wochen kein Tag passt (kein Wochentag gesetzt).
 */
export function naechsteLieferung(
  takt: { weekdays: number[]; interval_weeks: number; starts_on: string },
  fromIso: string
): string | null {
  if (!takt.weekdays.length) return null;
  const start = new Date(takt.starts_on + "T12:00:00");
  const iv = Math.max(1, takt.interval_weeks);
  for (let i = 0; i < 98; i++) {
    const d = new Date(fromIso + "T12:00:00");
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    if (iso < takt.starts_on) continue;
    const wd = ((d.getDay() + 6) % 7) + 1; // Mo=1 … So=7
    if (!takt.weekdays.includes(wd)) continue;
    const wochen = Math.floor(Math.round((d.getTime() - start.getTime()) / 86_400_000) / 7);
    if (wochen % iv === 0) return iso;
  }
  return null;
}
